import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { UserPlus, Film, Lock, Mail, User, Shield, AlertCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

const AdminRegister = () => {
  const navigate = useNavigate()
  const { isAuthenticated, hasPermission, sendEmail, currentAdmin } = useAuth()
  const { register, handleSubmit, formState: { errors }, watch } = useForm()
  const [isLoading, setIsLoading] = useState(false)

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login')
      toast.error('You must be logged in to register new admins')
      return
    }

    if (!hasPermission('canManageAdmins')) {
      navigate('/admin/dashboard')
      toast.error('You do not have permission to manage admins')
    }
  }, [isAuthenticated, hasPermission, navigate])

  const password = watch('password')

  const hashPassword = (password) => {
    let hash = 0
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(36)
  }

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')

      const usernameExists = admins.some(admin => admin.username === data.username)
      const emailExists = admins.some(admin => admin.email === data.email)

      if (usernameExists) {
        toast.error('Username already exists')
        setIsLoading(false)
        return
      }

      if (emailExists) {
        toast.error('Email already exists')
        setIsLoading(false)
        return
      }

      const newAdmin = {
        id: Date.now(),
        username: data.username,
        email: data.email,
        fullName: data.fullName,
        role: data.role,
        password: hashPassword(data.password),
        createdAt: new Date().toISOString(),
        createdBy: currentAdmin || 'admin',
        isActive: true
      }

      admins.push(newAdmin)
      localStorage.setItem('shikdar_films_admins', JSON.stringify(admins))

      // Send welcome email notification
      sendEmail({
        to: data.email,
        subject: 'Welcome to Shikdar Films Admin Panel',
        type: 'admin_registration',
        body: `Dear ${data.fullName},\n\nYour admin account has been created successfully!\n\nUsername: ${data.username}\nRole: ${data.role}\n\nYou can now log in to the admin panel at: ${window.location.origin}/admin/login\n\nBest regards,\nShikdar Films Team`
      })

      // Log the activity
      const activities = JSON.parse(localStorage.getItem('shikdar_films_admin_activities') || '[]')
      activities.push({
        id: Date.now(),
        type: 'admin_registered',
        message: `New admin registered: ${data.username} (${data.fullName}) with role ${data.role}`,
        timestamp: new Date().toISOString(),
        performedBy: currentAdmin || 'admin'
      })
      localStorage.setItem('shikdar_films_admin_activities', JSON.stringify(activities))

      // Send notification to super admins
      const notifications = JSON.parse(localStorage.getItem('shikdar_films_notifications') || '[]')
      notifications.push({
        id: Date.now(),
        type: 'admin_registered',
        title: 'New Admin Registered',
        message: `${data.fullName} (${data.username}) has been registered as ${data.role}`,
        timestamp: new Date().toISOString(),
        read: false
      })
      localStorage.setItem('shikdar_films_notifications', JSON.stringify(notifications))

      toast.success(`Admin registered successfully! Welcome email sent to ${data.email}`)
      navigate('/admin/dashboard')
    } catch (error) {
      toast.error('Registration failed. Please try again.')
    }

    setIsLoading(false)
  }

  const roleDescriptions = {
    'super-admin': 'Full access to all features including user management and system settings',
    'admin': 'Can manage applications and content but cannot create/delete other admins',
    'editor': 'Can view and edit applications but cannot change statuses or delete',
    'viewer': 'Read-only access to view applications and analytics'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-bengal-green to-bengal-orange flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-bengal-green to-bengal-orange rounded-2xl flex items-center justify-center shadow-lg">
              <Film className="w-12 h-12 text-white" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 section-title mb-2">
              Register New Admin
            </h2>
            <p className="text-gray-600">
              Create a new administrator account with role-based permissions
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="fullName"
                    {...register('fullName', { required: 'Full name is required' })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    placeholder="Enter full name"
                  />
                </div>
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                    })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    placeholder="admin@shikdarfilms.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username *
                </label>
                <div className="relative">
                  <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="username"
                    {...register('username', { 
                      required: 'Username is required',
                      minLength: { value: 4, message: 'Username must be at least 4 characters' }
                    })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    placeholder="Enter username"
                  />
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Role *
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    id="role"
                    {...register('role', { required: 'Role is required' })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  >
                    <option value="">Select role</option>
                    <option value="super-admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>
            </div>

            {watch('role') && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-1">
                      {watch('role').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Permissions:
                    </p>
                    <p className="text-sm text-blue-700">
                      {roleDescriptions[watch('role')]}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    id="password"
                    {...register('password', { 
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' }
                    })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    placeholder="Enter password"
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    id="confirmPassword"
                    {...register('confirmPassword', { 
                      required: 'Please confirm password',
                      validate: value => value === password || 'Passwords do not match'
                    })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    placeholder="Confirm password"
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-700">
                  A welcome email with login credentials will be sent to the admin's email address upon registration.
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Registering...' : 'Register Admin'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/admin/dashboard"
              className="text-bengal-green hover:text-bengal-orange font-medium transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminRegister